import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { ROUTES } from 'shared/config/routes'
import { JURISDICTION_REPORTS } from './data/reportFixtures'
import { useReportsHub } from './hooks/useReportsHub'
import { useReportsPeriodControls } from './hooks/useReportsPeriodControls'
import { ReportsShell } from './components/ReportsShell/ReportsShell'
import { ReportIcon } from './components/ReportIcon/ReportIcon'
import { PeriodSelect } from './components/PeriodSelect/PeriodSelect'
import { REPORT_CARDS } from './helpers/reportCards'
import s from './styles/ReportsHub.module.scss'

export const ReportsHub = observer(() => {
	const navigate = useNavigate()
	const periodControls = useReportsPeriodControls()
	const [profileId] = useState('uae-dubai')
	const profile = JURISDICTION_REPORTS[profileId]
	const { status, summary, error } = useReportsHub({
		period: periodControls.appliedPeriod,
		jurisdictionId: profileId,
		customFrom: periodControls.appliedCustomFrom,
		customTo: periodControls.appliedCustomTo,
	})

	return (
		<ReportsShell
			title="Reports & insights"
			// Same glyph as the Reports item in the sidebar footer navigation.
			icon={<span className="material-icons-outlined">insert_chart_outlined</span>}
			description="Understand student direction, reporting readiness and the support delivered by your team."
			actions={
				<div className={s['hub-period']}>
					<span className={s['hub-period__text']}>Default period</span>
					<PeriodSelect
						value={periodControls.selectedPeriod}
						onChange={periodControls.onPeriodChange}
						options={periodControls.periods}
						customFrom={periodControls.draftFrom}
						customTo={periodControls.draftTo}
						onCustomFromChange={periodControls.onCustomFromChange}
						onCustomToChange={periodControls.onCustomToChange}
						onApplyCustom={periodControls.onApplyCustom}
						canApplyCustom={periodControls.canApplyCustom}
						customRangeInvalid={periodControls.customRangeInvalid}
						triggerLabel={periodControls.triggerLabel}
						placeholder="Select period"
						minMenuWidth={240}
						menuZIndex={3000}
					/>
				</div>
			}
		>
			{status === 'error' && (
				<div className={s['hub-error']} role="alert">
					Could not load report summary{error ? `: ${error}` : '.'}
				</div>
			)}

			<div className={s['hub-intro']}>
				<div>
					<span className={s['hub-intro__eyebrow']}>Three simple views</span>
					<h2 className={s['hub-intro__title']}>Choose a report</h2>
				</div>
			</div>

			<div className={s['report-cards']}>
				{REPORT_CARDS.map(card => (
					<button
						key={card.id}
						type="button"
						className={`${s['report-card']} ${s[`report-card--${card.tone}`] || ''}`.trim()}
						onClick={() => navigate(`${ROUTES.REPORTS}${card.pathSuffix(profile)}`)}
						disabled={status === 'loading'}
					>
						<span className={s['report-card__icon']}>
							<ReportIcon name={card.icon} />
						</span>
						<span className={s['report-card__copy']}>
							<strong className={s['report-card__copy-title']}>{card.title}</strong>
							<span className={s['report-card__copy-question']}>{card.question}</span>
							<span className={s['report-card__metric']}>
								<b className={s['report-card__metric-value']}>
									{status === 'loading' ? '…' : card.getValue(summary)}
								</b>
								<small className={s['report-card__metric-label']}>{card.metric}</small>
							</span>
						</span>
						<span className={s['report-card__arrow']}>
							<ReportIcon name="arrow_forward" />
						</span>
					</button>
				))}
			</div>
		</ReportsShell>
	)
})

export default ReportsHub
